import type { FormattingRule, LabelFormattingInfo } from '@ardoq/data-model';
import type {
  APIFieldAttributes,
  APIFieldType,
  FieldGlobalAttributes,
  ViewIds,
} from '@ardoq/api-types';
import type { Locale } from '@ardoq/locale';
import type { PermissionContext } from '@ardoq/access-control';
import type { I18nLocale } from '@ardoq/i18n';
import {
  composeComponentFormattingGroups,
  composeReferencesFormattingGroups,
  ensureUpdatedRuleValue,
  getAddFormattingRuleButtonOptions,
  mapFilterGroups,
  mapFormatting,
  mapFormattingRuleInfo,
  mapListFieldFormatting,
  normalizeFormattingRules,
} from '@ardoq/perspectives';
import {
  MultiLabelFormattingProps,
  getAddLabelButtonProps,
} from '@ardoq/perspectives-sidebar';
import {
  ViewpointFormattingState,
  ViewpointFormattingProps,
} from './viewpointFormattingTypes';
import { disabledOptionProps } from 'viewpointBuilder/components/disabledOptionProps';
import { getFormattingForInvalidRuleByRuleId } from 'perspectiveSidebar/perspectiveEditor/getFormattingForInvalidRuleByRuleId';
import {
  getFormattingRuleNotificationParamsFunction,
  normalizeLegacyCheckboxComparator,
} from 'perspectiveSidebar/perspectiveEditor/formattingUtils';
import {
  getLabelFormattingElementProps,
  getFieldOptionLabel,
  getDisabledReferenceFormattingMessage,
  getRestrictedMultiLabelFormattingWarningProps,
} from 'perspectiveSidebar/perspectiveEditor/multiLabelUtils';
import { ViewpointBuilderFormattingCommands } from './viewpointBuilderFormattingCommands';
import { toFieldTypeRecord } from 'streams/fields/toFieldTypeRecord';

const buildFieldTypesByName = (
  ...fieldMaps: Map<string, APIFieldAttributes[]>[]
): Map<string, APIFieldType> => {
  const result = new Map<string, APIFieldType>();
  for (const fieldMap of fieldMaps) {
    for (const fields of fieldMap.values()) {
      for (const field of fields) {
        result.set(field.name, field.type);
      }
    }
  }
  return result;
};

const buildFieldLabelByName = (
  ...fieldMaps: Map<string, APIFieldAttributes[]>[]
): Map<string, string> => {
  const result = new Map<string, string>();
  for (const fieldMap of fieldMaps) {
    for (const fields of fieldMap.values()) {
      for (const field of fields) {
        result.set(field.name, getFieldOptionLabel(field));
      }
    }
  }
  return result;
};

const getMultiLabelProps = (
  labelFormatting: LabelFormattingInfo[],
  showReferenceType: boolean,
  componentTypeNames: string[],
  referenceTypeNames: string[],
  componentFieldsByType: Map<string, APIFieldAttributes[]>,
  referenceFieldsByType: Map<string, APIFieldAttributes[]>,
  viewName: ViewIds,
  permissionContext: PermissionContext,
  i18nLocale: I18nLocale,
  commands: ViewpointBuilderFormattingCommands
): MultiLabelFormattingProps => {
  const {
    onLabelFormattingReordered,
    onShowReferenceTypeToggled,
    onUpdateLabelFormattingAt,
    onLabelFormattingDeleted,
    onLabelFormattingAdded,
  } = commands;

  const componentFieldOptionsByTypes = createFieldOptionsByTypes(
    componentTypeNames,
    componentFieldsByType
  );
  const referenceFieldOptionsByTypes = createFieldOptionsByTypes(
    referenceTypeNames,
    referenceFieldsByType
  );

  const disableReferenceFormattingMessage =
    getDisabledReferenceFormattingMessage(viewName, i18nLocale);

  const fieldTypesByName = buildFieldTypesByName(
    componentFieldsByType,
    referenceFieldsByType
  );

  const fieldLabelByName = buildFieldLabelByName(
    componentFieldsByType,
    referenceFieldsByType
  );

  const labelFormattingElementProps = getLabelFormattingElementProps(
    labelFormatting,
    componentFieldOptionsByTypes,
    referenceFieldOptionsByTypes,
    Boolean(disableReferenceFormattingMessage),
    permissionContext,
    fieldTypesByName,
    fieldLabelByName,
    i18nLocale
  );

  const addLabelButtonProps = getAddLabelButtonProps({
    onLabelFormattingAdded: onLabelFormattingAdded,
    hasNoReferences: !referenceFieldOptionsByTypes.size,
    disableReferenceFormattingMessage,
  });

  const restrictedMultiLabelFormattingWarningProps =
    getRestrictedMultiLabelFormattingWarningProps({
      labelFormatting,
      viewId: viewName,
    });

  return {
    labelFormatting,
    showReferenceType,
    disableReferenceFormattingMessage,
    labelFormattingElementProps,
    addLabelButtonProps,
    restrictedMultiLabelFormattingWarningProps,
    onLabelFormattingReordered,
    onShowReferenceTypeToggled,
    onUpdateLabelFormattingAt,
    onLabelFormattingDeleted,
    onLabelFormattingAdded,
  };
};

const createFieldOptionsByTypes = (
  typeNames: string[],
  fieldsByType: Map<string, APIFieldAttributes[]>
) =>
  new Map(
    typeNames.map(typeName => [
      typeName,
      (fieldsByType.get(typeName) || []).map(field => ({
        value: field.name,
        label: getFieldOptionLabel(field),
      })),
    ])
  );

export const mapToViewpointBuilderFormattingViewState = (
  {
    viewName,
    formattingTab: {
      showReferenceType,
      labelFormatting,
      conditionalFormattingRules: rawConditionalFormattingRules,
      keepOriginalColor,
    },
    viewpointBuilderFormattingOptions: {
      asyncSuggestionsLoaders,
      availableComponentFields,
      availableReferenceFields,
      componentFieldsByType,
      referenceFieldsByType,
      componentNameAndFieldsIndex,
      referenceNameAndFieldsIndex,
      componentTypeNames,
      referenceTypeNames,
      tagOptions,
      asyncLabelLoaders,
    },
  }: ViewpointFormattingState,
  locale: Locale,
  permissionContext: PermissionContext,
  i18nLocale: I18nLocale,
  fieldDataByRuleId: Record<string, FieldGlobalAttributes | null>,
  fieldsByName: Map<string, APIFieldAttributes>,
  commands: ViewpointBuilderFormattingCommands
): ViewpointFormattingProps => {
  const {
    onFormattingRuleDeleted,
    onFormattingRuleReordered,
    onFormattingRuleAdded,
    onFormattingRuleUpdated,
  } = commands;
  const isNewConditionalFormatting = true;

  const conditionalFormattingRules = normalizeFormattingRules(
    rawConditionalFormattingRules
  );

  const componentsFormattingGroups = composeComponentFormattingGroups({
    i18nLocale,
    relatedFields: availableComponentFields,
    componentTypeNames: componentTypeNames,
    isNewConditionalFormatting,
    locale,
  });

  const referencesFormattingGroups = composeReferencesFormattingGroups({
    i18nLocale,
    relatedFields: availableReferenceFields,
    referenceTypeNames: referenceTypeNames,
    isNewConditionalFormatting,
    locale,
  });

  const formattingForInvalidRuleByRuleId = getFormattingForInvalidRuleByRuleId({
    conditionalFormattingRules,
    componentsFormattingGroups,
    referencesFormattingGroups,
    fieldDataByRuleId,
    locale,
  });

  const getFormattingForInvalidRule = (ruleId: string) =>
    formattingForInvalidRuleByRuleId[ruleId];

  const getRuleNotificationParams = getFormattingRuleNotificationParamsFunction(
    {
      componentsFormattingGroups,
      referencesFormattingGroups,
      getFormattingForInvalidRule,
      isNewConditionalFormatting,
      i18nLocale,
    }
  );

  const addRuleButtonOptions = getAddFormattingRuleButtonOptions({
    onFormattingRuleAdded,
    referencesFormattingGroups,
    tagsFormattingGroups: tagOptions,
    getDisabledOptionPopoverContent:
      disabledOptionProps.getAddButtonDisabledPopoverContent,
    isNewConditionalFormatting,
    i18nLocale,
  });

  const filterGroupsByRuleId = mapFilterGroups({
    i18nLocale,
    conditionalFormattingRules,
    componentsFormattingGroups,
    referencesFormattingGroups,
    formattingForInvalidRuleByRuleId,
    componentNameAndFieldsIndex,
    referenceNameAndFieldsIndex,
    isNewConditionalFormatting,
  });
  const formattingByRuleAndField = mapFormatting(filterGroupsByRuleId);
  const listFieldFormattingByRuleId = mapListFieldFormatting(
    conditionalFormattingRules,
    formattingByRuleAndField
  );
  const ruleInfo = mapFormattingRuleInfo(
    i18nLocale,
    conditionalFormattingRules,
    filterGroupsByRuleId,
    formattingByRuleAndField,
    listFieldFormattingByRuleId,
    isNewConditionalFormatting,
    toFieldTypeRecord(fieldsByName)
  );

  const formattingRules = normalizeLegacyCheckboxComparator(
    conditionalFormattingRules,
    fieldDataByRuleId,
    locale
  );

  const onRuleUpdated = (
    updatedFormattingRuleId: string,
    updatedFormattingRulePayload: Partial<FormattingRule>
  ) => {
    const ruleFieldFormatting =
      ruleInfo?.[updatedFormattingRuleId]?.fieldFormatting;

    const fieldFormattingData = {
      i18nLocale,
      updatedFormattingRulePayload,
      componentsFormattingGroups,
      referencesFormattingGroups,
      formattingForInvalidRuleByRuleId,
      componentNameAndFieldsIndex,
      referenceNameAndFieldsIndex,
      isNewConditionalFormatting,
      viewId: viewName,
    };

    return onFormattingRuleUpdated(
      updatedFormattingRuleId,
      ensureUpdatedRuleValue({
        previousFormattingRules: conditionalFormattingRules,
        updatedFormattingRuleId,
        updatedFormattingRulePayload,
        ruleFieldFormatting,
        fieldFormattingData,
      })
    );
  };

  const { setKeepOriginalColor } = commands;
  return {
    multiLabelFormatting: getMultiLabelProps(
      labelFormatting,
      showReferenceType,
      componentTypeNames,
      referenceTypeNames,
      componentFieldsByType,
      referenceFieldsByType,
      viewName,
      permissionContext,
      i18nLocale,
      commands
    ),
    conditionalFormatting: {
      formattingRules,
      asyncSuggestionsLoaders,
      asyncLabelLoaders,
      tagsFormattingGroups: tagOptions,
      getRuleNotificationParams,
      disabledOptionProps,
      getFormattingForInvalidRule,
      onFormattingRuleDeleted,
      onFormattingRuleReordered,
      onFormattingRuleUpdated: onRuleUpdated,
      onKeepOriginalColorToggled: setKeepOriginalColor,
      keepOriginalColor,
      addRuleButtonOptions,
      ruleInfo,
      componentTypeNames,
      referenceTypeNames,
      viewId: viewName,
      locale,
      i18nLocale,
    },
  };
};
